
import React from 'react';
import { Interview } from '@/types/interview';
import PendingInterviewsTable from '@/components/interview/PendingInterviewsTable';
import EmptyInterviewsList from '@/components/interview/EmptyInterviewsList';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import InterviewDialogs from './InterviewDialogs';
import ConfirmationDialogs from './ConfirmationDialogs';
import { useInterviewActions } from './useInterviewActions';

interface FeederInterviewsListProps {
  interviews: Interview[];
  isLoading: boolean;
  error: unknown;
  onViewInterview: (interview: Interview) => void;
  onEditInterview: (interview: Interview) => void;
  onPublish: (interviewId: string) => Promise<void>;
  onSendToFeeder: (interviewId: string) => Promise<void>;
  publishingId: string | null;
  selectedInterview: Interview | null;
  isViewDialogOpen: boolean;
  isEditDialogOpen: boolean;
  handleCloseViewDialog: () => void;
  handleCloseEditDialog: () => void;
}

const FeederInterviewsList: React.FC<FeederInterviewsListProps> = ({
  interviews,
  isLoading,
  error,
  onViewInterview,
  onEditInterview,
  onPublish,
  onSendToFeeder,
  publishingId,
  selectedInterview,
  isViewDialogOpen,
  isEditDialogOpen,
  handleCloseViewDialog,
  handleCloseEditDialog,
}) => {
  const {
    isPublishConfirmOpen,
    isDeleteConfirmOpen,
    setIsPublishConfirmOpen,
    setIsDeleteConfirmOpen,
    deletingId,
    confirmPublish,
    confirmDelete,
    handleConfirmedPublish,
    handleConfirmedDelete
  } = useInterviewActions(handleCloseViewDialog, handleCloseEditDialog, onPublish);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!interviews || interviews.length === 0) {
    return <EmptyInterviewsList message="No interviews in the feeder queue yet." />;
  }

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Feeder Interviews ({interviews.length})</h3>
      <PendingInterviewsTable 
        interviews={interviews} 
        onViewInterview={onViewInterview}
        onEditInterview={onEditInterview}
        onPublish={confirmPublish}
        onSendToFeeder={onSendToFeeder}
        onDelete={confirmDelete}
        publishingId={publishingId}
        sendingToFeederId={null}
        deletingId={deletingId}
      />

      <InterviewDialogs
        selectedInterview={selectedInterview}
        isViewDialogOpen={isViewDialogOpen}
        isEditDialogOpen={isEditDialogOpen}
        handleCloseViewDialog={handleCloseViewDialog}
        handleCloseEditDialog={handleCloseEditDialog}
        onPublish={onPublish}
        onSendToFeeder={onSendToFeeder}
        publishingId={publishingId}
      />

      <ConfirmationDialogs
        isPublishConfirmOpen={isPublishConfirmOpen}
        isDeleteConfirmOpen={isDeleteConfirmOpen}
        setIsPublishConfirmOpen={setIsPublishConfirmOpen}
        setIsDeleteConfirmOpen={setIsDeleteConfirmOpen}
        handleConfirmedPublish={handleConfirmedPublish}
        handleConfirmedDelete={handleConfirmedDelete}
      />
    </div>
  );
};

export default FeederInterviewsList;
